import {Component, HostBinding, inject, Input} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {LookupControls, LookupDefinition, lookupPickerName} from '../lookup-controls';

/**
 * A mapping row's Kodeoppslag inputs: the lookup the row takes its value
 * through, and the column of that lookup's table the value is. The column
 * select shows only once the picked lookup has a table, with a hint while
 * that table's columns are on their way.
 */
@Component({
  selector: 'app-lookup-source-fields',
  templateUrl: './lookup-source-fields.component.html',
  styleUrls: ['./lookup-source-fields.component.scss'],
  standalone: false,
})
export class LookupSourceFieldsComponent {
  private readonly translate: TranslateService = inject(TranslateService);

  @Input({required: true}) row!: LookupControls;
  @Input() lookups: readonly LookupDefinition[] = [];
  // Each lookup table's columns once fetched, and the tables whose
  // columns are still on their way.
  @Input() lookupColumns: { [table: string]: { name: string }[] } = {};
  @Input() lookupColumnsLoading: ReadonlySet<string> = new Set<string>();
  // Lays the two selects out side by side in a box of their own; without
  // it they are items of the host's parent row.
  @HostBinding('class.lookup-source-fields--grid')
  @Input() grid = false;

  /** A lookup's name in the picker (`lookupPickerName`). */
  lookupName(lookup: LookupDefinition): string {
    return lookupPickerName(lookup, key => this.translate.instant(key));
  }

  /** The value column's options: the picked lookup's table's columns,
   * empty while they are on their way; `null` while the row names no
   * lookup with a table, and the value column select does not show. */
  valueColumns(): { name: string }[] | null {
    const table = this.tableOf();
    return table ? this.lookupColumns[table] ?? [] : null;
  }

  /** Whether the picked lookup's table's columns are still being fetched. */
  isLoadingColumns(): boolean {
    const table = this.tableOf();
    return !!table && this.lookupColumnsLoading.has(table);
  }

  /** The table of the picked lookup - where the value column is picked
   * from - or `null` while the row names no lookup with a table. */
  private tableOf(): string | null {
    return this.lookups.find(lookup => lookup.key === this.row.lookupRefControl.value)?.tableControl.value || null;
  }
}
